import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  KeyRound,
  Mail,
  RotateCcw,
  Trash2,
} from "lucide-react";
import SectionCard from "components/SectionCard";
import ModalBackdrop from "components/ModalBackdrop";
import { useDialog } from "context/DialogContext";
import { useProfile } from "context/ProfileContext";
import { supabase } from "services/supabaseClient";
import { callFunction } from "lib/edge";
import { logAudit } from "lib/audit";

function normStr(x) {
  return String(x ?? "").trim();
}

function asLower(x) {
  return normStr(x).toLowerCase();
}

function compareValues(aVal, bVal, asc = true) {
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return asc ? 1 : -1;
  if (bVal == null) return asc ? -1 : 1;

  const av = typeof aVal === "boolean" ? Number(aVal) : aVal;
  const bv = typeof bVal === "boolean" ? Number(bVal) : bVal;

  if (typeof av === "number" && typeof bv === "number") {
    if (av < bv) return asc ? -1 : 1;
    if (av > bv) return asc ? 1 : -1;
    return 0;
  }

  const as = asLower(av);
  const bs = asLower(bv);

  if (as < bs) return asc ? -1 : 1;
  if (as > bs) return asc ? 1 : -1;
  return 0;
}

function profileName(profile) {
  const first = normStr(profile?.first_name);
  const last = normStr(profile?.last_name);
  const name = [first, last].filter(Boolean).join(" ");
  return name || "(no profile)";
}

function userEmail(user) {
  return normStr(user?.profile?.email || user?.email);
}

function userType(profile) {
  if (profile?.is_admin) return "Platform Admin";
  if (profile?.is_org_admin) return "Org Admin";
  return "User";
}

function formatDateTime(value) {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.valueOf())) return "Never";
  return d.toLocaleString();
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.valueOf())) return "";
  return d.toLocaleDateString();
}

function formatShortDateTime(value) {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.valueOf())) return "Never";
  return d.toLocaleDateString([], {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
}

function statusLabel(user) {
  if (user.suspended) return "Suspended";
  if (user.confirmed_at || user.email_confirmed_at) return "Active";
  if (user.invited_at) return "Invited";
  return "Unconfirmed";
}

function isAuthSuspended(user) {
  const bannedUntil = user?.banned_until ? new Date(user.banned_until) : null;

  return (
    bannedUntil instanceof Date &&
    !Number.isNaN(bannedUntil.valueOf()) &&
    bannedUntil.getTime() > Date.now()
  );
}

function uniqLabels(values) {
  return Array.from(new Set(values.map(normStr).filter(Boolean)));
}

function yesNo(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "";
}

function DetailItem({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium uppercase tracking-wide text-muted">
        {label}
      </div>
      <div className="mt-0.5 break-words text-sm text-app">{value || "—"}</div>
    </div>
  );
}

export default function Users() {
  const { profile, loading: profileLoading } = useProfile();
  const { alert, confirm } = useDialog();

  const isPlatformAdmin = !!profile?.is_admin;
  const currentUserId = profile?.id;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState("");
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("last_sign_in");
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");

  const loadUsers = useCallback(async () => {
    if (!isPlatformAdmin) return;

    setLoading(true);

    try {
      const [authResult, profilesResult, orgLinksResult, appLinksResult] =
        await Promise.all([
          callFunction("admin-users", {
            body: { action: "list" },
          }),
          supabase
            .from("profiles")
            .select(
              "id, company, phone, address, created_at, email, first_name, last_name, city, state, zip_postcode, is_financial_pro, is_admin, trade_tracker_access, market_data_access, mfa_enabled, tenant_id, is_org_admin, disabled, terms_accepted_at, terms_version"
            )
            .order("last_name", { ascending: true }),
          supabase
            .from("org_user_links")
            .select(
              "user_id, org_id, role, effective_from, effective_to, mfa_required, mfa_required_at, mfa_required_by, orgs(id, name)"
            ),
          supabase
            .from("user_app_access")
            .select("user_id, apps(id, name, slug)"),
        ]);

      if (profilesResult.error) throw profilesResult.error;
      if (orgLinksResult.error) throw orgLinksResult.error;
      if (appLinksResult.error) throw appLinksResult.error;

      const profilesById = new Map(
        (profilesResult.data || []).map((p) => [p.id, p])
      );
      const orgsByUserId = new Map();
      const appsByUserId = new Map();

      for (const link of orgLinksResult.data || []) {
        const rows = orgsByUserId.get(link.user_id) || [];
        rows.push({
          org_id: link.org_id,
          org_name: link.orgs?.name || "",
          role: link.role || "",
          effective_from: link.effective_from || null,
          effective_to: link.effective_to || null,
          mfa_required: !!link.mfa_required,
          mfa_required_at: link.mfa_required_at || null,
          mfa_required_by: link.mfa_required_by || null,
        });
        orgsByUserId.set(link.user_id, rows);
      }

      for (const link of appLinksResult.data || []) {
        const rows = appsByUserId.get(link.user_id) || [];
        if (link.apps) rows.push(link.apps);
        appsByUserId.set(link.user_id, rows);
      }

      setUsers(
        (authResult?.users || []).map((u) => {
          const userProfile = profilesById.get(u.id) || null;

          return {
            ...u,
            profile: userProfile,
            org_links: orgsByUserId.get(u.id) || [],
            apps: appsByUserId.get(u.id) || [],
            suspended: isAuthSuspended(u) || !!userProfile?.disabled,
          };
        })
      );
    } catch (err) {
      await alert(err.message || String(err), {
        title: "Users",
      });
    } finally {
      setLoading(false);
    }
  }, [alert, isPlatformAdmin]);

  useEffect(() => {
    if (profileLoading) return;
    void loadUsers();
  }, [loadUsers, profileLoading]);

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "User",
        className: "w-[28%]",
        sortValue: (u) =>
          `${normStr(u.profile?.last_name)} ${normStr(
            u.profile?.first_name
          )} ${userEmail(u)}`,
      },
      {
        key: "status",
        header: "Status",
        className: "w-[11%]",
        sortValue: (u) => statusLabel(u),
      },
      {
        key: "role",
        header: "Role",
        className: "w-[16%]",
        sortValue: (u) => userType(u.profile),
      },
      {
        key: "orgs",
        header: "Orgs",
        className: "w-[23%]",
        sortValue: (u) =>
          uniqLabels((u.org_links || []).map((l) => l.org_name)).join(" "),
      },
      {
        key: "last_sign_in",
        header: "Last Login",
        className: "w-[14%]",
        sortValue: (u) => u.last_sign_in_at || "",
      },
      {
        key: "details",
        header: "Details",
        className: "w-[8%]",
        sortValue: null,
      },
    ],
    []
  );

  const filteredUsers = useMemo(() => {
    const q = asLower(query);
    if (!q) return users;

    return users.filter((u) => {
      const p = u.profile || {};
      const orgs = uniqLabels((u.org_links || []).map((l) => l.org_name));
      const apps = uniqLabels((u.apps || []).map((a) => a.name || a.slug));

      return [
        profileName(p),
        userEmail(u),
        p.company,
        p.phone,
        p.city,
        p.state,
        userType(p),
        statusLabel(u),
        ...orgs,
        ...apps,
      ]
        .map(asLower)
        .some((v) => v.includes(q));
    });
  }, [query, users]);

  const sortedUsers = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    const list = [...filteredUsers];

    if (!col || typeof col.sortValue !== "function") return list;

    list.sort((a, b) =>
      compareValues(col.sortValue(a), col.sortValue(b), sortAsc)
    );

    return list;
  }, [columns, filteredUsers, sortAsc, sortKey]);

  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    return users.find((u) => u.id === selectedUserId) || null;
  }, [selectedUserId, users]);

  const handleSortClick = (col) => {
    if (!col || typeof col.sortValue !== "function") return;

    if (sortKey === col.key) {
      setSortAsc((v) => !v);
      return;
    }

    setSortKey(col.key);
    setSortAsc(true);
  };

  const renderSortIcon = (col) => {
    if (!col || typeof col.sortValue !== "function") return null;
    if (sortKey !== col.key) return <ArrowUpDown className="h-3.5 w-3.5" />;

    return sortAsc ? (
      <ArrowUp className="h-3.5 w-3.5 text-app" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 text-app" />
    );
  };

  const runUserAction = async (user, action, opts = {}) => {
    const email = userEmail(user);

    if (opts.confirmMessage) {
      const ok = await confirm(opts.confirmMessage, {
        title: opts.title || "Confirm",
        okLabel: opts.okLabel || "OK",
      });

      if (!ok) return;
    }

    setBusyUserId(user.id);

    try {
      const redirectTo =
        action === "password_reset"
          ? `${window.location.origin}/reset-password`
          : `${window.location.origin}/signup-user?email=${encodeURIComponent(
              email
            )}`;

      await callFunction("admin-users", {
        body: {
          action,
          user_id: user.id,
          email,
          redirect_to: redirectTo,
        },
      });

      if (action === "suspend" || action === "reactivate") {
        const { error: profileUpdateError } = await supabase
          .from("profiles")
          .update({ disabled: action === "suspend" })
          .eq("id", user.id);

        if (profileUpdateError) throw profileUpdateError;
      }

      await logAudit({
        category: "user",
        action: `admin_${action}`,
        success: true,
        target_user_id: user.id,
        detail: { email },
      });

      await loadUsers();

      if (opts.successMessage) {
        await alert(opts.successMessage, { title: opts.title || "Users" });
      }

      return true;
    } catch (err) {
      await logAudit({
        category: "user",
        action: `admin_${action}_failed`,
        success: false,
        target_user_id: user.id,
        detail: { email, error: err.message || String(err) },
      });

      await alert(err.message || String(err), {
        title: opts.title || "Users",
      });

      return false;
    } finally {
      setBusyUserId("");
    }
  };

  if (profileLoading || loading) {
    return <div className="text-app">Loading users...</div>;
  }

  if (!isPlatformAdmin) {
    return <div className="text-app">Platform admin access required.</div>;
  }

  return (
    <div className="space-y-6">
      <SectionCard>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-app">Users</div>
            <div className="text-sm text-muted">
              View/manage user profiles and account access.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 w-72 rounded-md border border-input bg-background px-3 text-sm text-foreground"
              placeholder="Search users..."
            />

            <button
              type="button"
              className="btn btn-ghost h-8 px-3 text-sm"
              onClick={loadUsers}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-app">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-app text-muted shadow-[inset_0_-1px_0_hsl(var(--border))]">
              <tr className="select-none">
                {columns.map((col) => {
                  const canSort = typeof col.sortValue === "function";

                  return (
                    <th
                      key={col.key}
                      className={[
                        "px-4 py-2 text-left font-bold",
                        canSort ? "cursor-pointer" : "",
                        col.key === "details" ? "text-right" : "",
                        col.className || "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => handleSortClick(col)}
                    >
                      <div
                        className={[
                          "flex min-w-0 items-center gap-1",
                          col.key === "details" ? "justify-end" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <span className="truncate">{col.header}</span>
                        {renderSortIcon(col)}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {sortedUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-sm italic text-muted"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                sortedUsers.map((u) => {
                  const p = u.profile || {};
                  const email = userEmail(u);
                  const isSelf = u.id === currentUserId;
                  const orgLabels = uniqLabels(
                    (u.org_links || []).map((l) => l.org_name)
                  );

                  return (
                    <tr
                      key={u.id}
                      className="border-t border-app align-top hover:bg-app/50"
                    >
                      <td className="px-4 py-3">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-app">
                            {profileName(p)}
                            {isSelf && (
                              <span className="font-normal text-muted">
                                {" "}
                                (you)
                              </span>
                            )}
                          </div>
                          <div className="truncate text-xs text-muted" title={email}>
                            {email || "(no email)"}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={[
                            "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                            u.suspended
                              ? "bg-red-100 text-red-700"
                              : "bg-emerald-100 text-emerald-700",
                          ].join(" ")}
                        >
                          {statusLabel(u)}
                        </span>
                        {u.banned_until && (
                          <div className="mt-1 text-xs text-muted">
                            Until {formatDate(u.banned_until)}
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-3 text-app">
                        <div className="truncate">{userType(p)}</div>
                        <div className="text-xs text-muted">
                          MFA {p.mfa_enabled ? "enabled" : "off"}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-app">
                        <div
                          className="truncate"
                          title={orgLabels.length ? orgLabels.join(", ") : "No orgs"}
                        >
                          {orgLabels.length ? orgLabels.join(", ") : "No orgs"}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-app">
                        <div>{formatShortDateTime(u.last_sign_in_at)}</div>
                        <div className="text-xs text-muted">
                          Created {formatDate(u.created_at) || "unknown"}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          className="btn btn-ghost h-8 px-2 text-sm"
                          onClick={() => setSelectedUserId(u.id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          currentUserId={currentUserId}
          busy={busyUserId === selectedUser.id}
          onClose={() => setSelectedUserId("")}
          onAction={async (user, action, opts) => {
            const ok = await runUserAction(user, action, opts);
            if (ok && action === "delete") setSelectedUserId("");
          }}
        />
      )}
    </div>
  );
}

function UserDetailsModal({
  user,
  currentUserId,
  busy,
  onClose,
  onAction,
}) {
  const p = user.profile || {};
  const email = userEmail(user);
  const isSelf = user.id === currentUserId;
  const orgLabels = uniqLabels((user.org_links || []).map((l) => l.org_name));
  const appLabels = uniqLabels((user.apps || []).map((a) => a.name || a.slug));
  const mfaRequiredOrgLabels = uniqLabels(
    (user.org_links || [])
      .filter((l) => l.mfa_required)
      .map((l) => l.org_name || l.org_id)
  );
  const effectiveMfaRequired = mfaRequiredOrgLabels.length > 0;

  return (
    <ModalBackdrop>
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-lg border border-app bg-app p-5 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={`User details for ${profileName(p)}`}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-lg font-semibold text-app">
              {profileName(p)}
              {isSelf && <span className="font-normal text-muted"> (you)</span>}
            </div>
            <div className="truncate text-sm text-muted">{email || "No email"}</div>
          </div>

          <button
            type="button"
            className="btn btn-ghost h-8 px-3 text-sm"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="mb-5 flex flex-wrap gap-2">
          <span
            className={[
              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
              user.suspended
                ? "bg-red-100 text-red-700"
                : "bg-emerald-100 text-emerald-700",
            ].join(" ")}
          >
            {statusLabel(user)}
          </span>
          <span className="inline-flex rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-app">
            {userType(p)}
          </span>
          <span className="inline-flex rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-app">
            MFA {p.mfa_enabled ? "enabled" : "off"}
          </span>
          <span
            className={[
              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
              effectiveMfaRequired
                ? "bg-amber-100 text-amber-800"
                : "bg-white/10 text-app",
            ].join(" ")}
          >
            MFA {effectiveMfaRequired ? "required" : "optional"}
          </span>
          <span className="inline-flex rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-app">
            Terms {p.terms_accepted_at ? "accepted" : "pending"}
          </span>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-4">
            <div className="text-sm font-semibold text-app">Profile</div>
            <div className="grid gap-3">
              <DetailItem label="Profile ID" value={p.id || user.id} />
              <DetailItem label="Email" value={email} />
              <DetailItem label="First Name" value={p.first_name} />
              <DetailItem label="Last Name" value={p.last_name} />
              <DetailItem label="Company" value={p.company} />
              <DetailItem label="Phone" value={p.phone} />
              <DetailItem label="Address" value={p.address} />
              <DetailItem label="City" value={p.city} />
              <DetailItem label="State" value={p.state} />
              <DetailItem label="Zip / Postcode" value={p.zip_postcode} />
              <DetailItem
                label="Financial Professional"
                value={yesNo(p.is_financial_pro)}
              />
              <DetailItem label="Platform Admin" value={yesNo(p.is_admin)} />
              <DetailItem label="Org Admin" value={yesNo(p.is_org_admin)} />
              <DetailItem
                label="TradeTracker Access"
                value={yesNo(p.trade_tracker_access)}
              />
              <DetailItem
                label="Market Data Access"
                value={yesNo(p.market_data_access)}
              />
              <DetailItem label="MFA Enabled" value={yesNo(p.mfa_enabled)} />
              <DetailItem label="Disabled" value={yesNo(p.disabled)} />
              <DetailItem
                label="Terms Accepted At"
                value={formatDateTime(p.terms_accepted_at)}
              />
              <DetailItem label="Terms Version" value={p.terms_version} />
              <DetailItem label="Tenant ID" value={p.tenant_id} />
              <DetailItem
                label="Profile Created"
                value={formatDateTime(p.created_at)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="text-sm font-semibold text-app">Access</div>
            <div className="grid gap-3">
              <DetailItem
                label="Organizations"
                value={orgLabels.length ? orgLabels.join(", ") : "No orgs"}
              />
              <DetailItem
                label="MFA Required"
                value={effectiveMfaRequired ? "Yes" : "No"}
              />
              <DetailItem
                label="MFA Required By"
                value={
                  mfaRequiredOrgLabels.length
                    ? mfaRequiredOrgLabels.join(", ")
                    : ""
                }
              />
              <DetailItem
                label="Apps"
                value={
                  p.is_admin
                    ? "Platform access"
                    : appLabels.length
                    ? appLabels.join(", ")
                    : "No app grants"
                }
              />
              <DetailItem label="Created" value={formatDateTime(user.created_at)} />
              <DetailItem label="Invited" value={formatDateTime(user.invited_at)} />
              <DetailItem
                label="Confirmed"
                value={formatDateTime(user.confirmed_at || user.email_confirmed_at)}
              />
              <DetailItem
                label="Last Login"
                value={formatDateTime(user.last_sign_in_at)}
              />
              <DetailItem
                label="Suspended Until"
                value={user.banned_until ? formatDateTime(user.banned_until) : ""}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-app pt-4">
          <div className="mb-3 text-sm font-semibold text-app">Admin Actions</div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-ghost h-8 px-3 text-sm"
              disabled={busy || !email}
              onClick={() =>
                onAction(user, "magic_link", {
                  title: "Magic link",
                  successMessage: "Magic link sent.",
                })
              }
            >
              <Mail className="mr-2 h-4 w-4" />
              Magic Link
            </button>

            <button
              type="button"
              className="btn btn-ghost h-8 px-3 text-sm"
              disabled={busy || !email}
              onClick={() =>
                onAction(user, "password_reset", {
                  title: "Password reset",
                  successMessage: "Password reset email sent.",
                })
              }
            >
              <KeyRound className="mr-2 h-4 w-4" />
              Reset Password
            </button>

            {user.suspended ? (
              <button
                type="button"
                className="btn btn-ghost h-8 px-3 text-sm"
                disabled={busy || isSelf}
                onClick={() =>
                  onAction(user, "reactivate", {
                    title: "Reactivate user",
                    confirmMessage: `Reactivate ${email}?`,
                    okLabel: "Reactivate",
                  })
                }
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reactivate
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-ghost h-8 px-3 text-sm text-red-600"
                disabled={busy || isSelf}
                onClick={() =>
                  onAction(user, "suspend", {
                    title: "Suspend user",
                    confirmMessage: `Suspend ${email}? This blocks future login until reactivated.`,
                    okLabel: "Suspend",
                  })
                }
              >
                <Ban className="mr-2 h-4 w-4" />
                Suspend
              </button>
            )}

            <button
              type="button"
              className="btn btn-ghost h-8 px-3 text-sm text-red-600"
              disabled={busy || isSelf}
              onClick={() =>
                onAction(user, "delete", {
                  title: "Delete user",
                  confirmMessage: `Permanently delete ${email}? Prefer suspend for normal offboarding.`,
                  okLabel: "Delete",
                })
              }
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}
